import React from 'react';

import { FormControl, FormHelperText, FormLabel, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { GridProps } from '@mui/material/Grid/Grid';
import { DatePicker, PickersTextField } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { useField } from 'formik';
import { Calendar } from 'iconsax-reactjs';

import { ControlSx } from '../../types/control.type';

function OpenPickerIcon() {
  return <Calendar width={16} height={16} size={16} />;
}

const StyledPickersTextField = styled(PickersTextField)({
  '& .MuiPickersInputBase-root': {
    borderRadius: 8,
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    color: '#3B4C61',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#E1E7EF',
    },
    '&:hover fieldset': {
      borderColor: '#A9B3FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#A9B3FF',
      borderWidth: '1px',
    },
    '& .MuiPickersSectionList-root': {
      padding: '8px 0',
    },
    '& .MuiInputAdornment-root .MuiButtonBase-root': {
      padding: '4px',
      height: '24px',
      marginRight: '-9px',
    },
  },
});

type IProps = ControlSx & {
  label?: string;
  name: string;
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  gridProps?: GridProps;
  fullWidth?: boolean;
  disabled?: boolean;
};

const FormDate = React.memo(
  ({
    label,
    name,
    value,
    defaultValue,
    controlSx = {},
    gridProps = {},
    fullWidth = true,
    disabled = false,
    ...props
  }: IProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers] = useField(name);

    const isError = meta.touched && Boolean(meta.error);

    return (
      <Grid {...gridProps}>
        <FormControl fullWidth={fullWidth} sx={{ minWidth: 250, ...controlSx }}>
          <FormLabel>{label}</FormLabel>

          <DatePicker
            {...field}
            defaultValue={defaultValue}
            onChange={(selectValue) => helpers.setValue(selectValue)}
            disabled={disabled}
            slots={{
              textField: StyledPickersTextField,
              openPickerIcon: OpenPickerIcon,
            }}
            slotProps={{
              textField: {
                error: isError,
                fullWidth: true,
              },
            }}
            {...props}
          />

          {isError && <FormHelperText error>{meta.error}</FormHelperText>}
        </FormControl>
      </Grid>
    );
  },
);

export default FormDate;
